/**
 * The reviewer set, rendered as the two sets §7.6 says it is.
 *
 * The acceptance criterion this file is written against is a negative one: a
 * screen that says "2 of 3 approved" without saying which of the three is
 * required has lost the entire distinction §7.6 exists to draw. So the two
 * groups are never merged, never sorted into one list, and never counted
 * together — {@link ReviewerTally} cannot even produce a number that spans them,
 * because the sentence it builds names the required set and then names the
 * advisory one separately.
 *
 * Everything here reports; nothing decides. Whether a removal is allowed is
 * `document.approve` resolved in SQL inside the mutating transaction, and the
 * API refuses the owner and anyone who has already decided with a sentence of
 * its own. This file offers the control and renders the refusal — a client that
 * hid the button according to its own reading of the rules would be a second
 * copy of them, and the copy nothing tests.
 */

import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import type { ApprovalReviewer } from '../../api/types.ts';
import { formatDateTime } from '../../components/format.ts';
import { Badge, StatusBadge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Mono } from '../../components/ui/layout.tsx';
import { Table, TableEmpty, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';
import { DecisionBadge, RequirementBadge } from './vocabulary.tsx';

export interface ReviewerTallyCounts {
  required: number;
  requiredApproved: number;
  requiredOutstanding: number;
  requiredRejected: number;
  advisory: number;
  advisoryOutstanding: number;
}

export function tallyReviewers(reviewers: readonly ApprovalReviewer[]): ReviewerTallyCounts {
  const required = reviewers.filter((reviewer) => reviewer.requirement === 'required');
  const advisory = reviewers.filter((reviewer) => reviewer.requirement === 'advisory');
  return {
    required: required.length,
    requiredApproved: required.filter((reviewer) => reviewer.decision === 'approved').length,
    requiredOutstanding: required.filter((reviewer) => reviewer.decision === null).length,
    requiredRejected: required.filter((reviewer) => reviewer.decision === 'rejected').length,
    advisory: advisory.length,
    advisoryOutstanding: advisory.filter((reviewer) => reviewer.decision === null).length,
  };
}

/**
 * The headline number, with the word "required" welded to it.
 *
 * There is deliberately no way to call this and get a bare "2 of 3": the count
 * is over the required set only, and the advisory clause is a second sentence
 * that states it is not a gate. That is the whole distinction, in the one place
 * a person actually reads.
 *
 * A block `span` rather than a `p`, because one of its two callers is a
 * `CardHeader` description — which wraps whatever it is given in a paragraph of
 * its own, and a nested `p` is invalid markup the browser silently unnests.
 */
export function ReviewerTally({ reviewers }: { reviewers: readonly ApprovalReviewer[] }) {
  const counts = tallyReviewers(reviewers);

  return (
    <span className="block text-sm text-secondary">
      <span className="font-semibold text-primary">
        {counts.requiredApproved} of {counts.required} required{' '}
        {counts.required === 1 ? 'reviewer has' : 'reviewers have'} approved
      </span>
      {counts.requiredRejected > 0 ? (
        <span className="font-semibold text-error-primary">
          {' '}
          · {counts.requiredRejected} rejected
        </span>
      ) : null}
      {counts.advisory > 0 ? (
        <>
          {' · '}
          {counts.advisoryOutstanding} of {counts.advisory} advisory{' '}
          {counts.advisory === 1 ? 'reviewer has' : 'reviewers have'} not answered, which does not
          block publishing
        </>
      ) : null}
      {counts.required === 0 ? (
        <span className="text-warning-primary">
          {' '}
          — and with no required reviewer there is nothing for this to attest to (§7.6).
        </span>
      ) : null}
    </span>
  );
}

/**
 * One reviewer's identity: who they are, and the two facts about them that
 * change how the row reads.
 *
 * `isOwner` is §7.3's auto-added row, resolved against who owns the document
 * *now* — so on a document that changed hands mid-review nobody carries the
 * marker, which is the honest rendering rather than a bug. §2.6's status is
 * shown for the same reason the API carries it: a deactivated required reviewer
 * resolves to denied everywhere, so their silence is permanent and has to read
 * as departure rather than as delay.
 */
function ReviewerIdentity({ reviewer, isYou }: { reviewer: ApprovalReviewer; isYou: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-2">
        {reviewer.name ? (
          <Link
            to="/admin/people/$personId"
            params={{ personId: reviewer.personId }}
            className="font-medium text-primary no-underline hover:underline"
          >
            {reviewer.name}
          </Link>
        ) : (
          <Mono>{reviewer.personId}</Mono>
        )}
        {isYou ? <Badge tone="brand">You</Badge> : null}
        {reviewer.isOwner ? (
          <Badge tone="outline">
            <span title="The document's owner, added as required by §7.3. Cannot be removed.">
              Owner
            </span>
          </Badge>
        ) : null}
        {reviewer.userStatus && reviewer.userStatus !== 'active' ? (
          <StatusBadge status={reviewer.userStatus} />
        ) : null}
      </span>
      {reviewer.email ? <span className="text-xs text-tertiary">{reviewer.email}</span> : null}
    </div>
  );
}

interface ReviewerTableProps {
  reviewers: readonly ApprovalReviewer[];
  viewerId: string | undefined;
  /** Omitted on a closed request, where nothing about the set can change. */
  onRemove?: (personId: string) => void;
  removingId?: string | null;
}

function ReviewerRows({ reviewers, viewerId, onRemove, removingId }: ReviewerTableProps) {
  return (
    <Table>
      <THead>
        <TR>
          <TH>Reviewer</TH>
          <TH>Decision</TH>
          <TH>When</TH>
          <TH>Added</TH>
          {onRemove ? <TH>{''}</TH> : null}
        </TR>
      </THead>
      <TBody>
        {reviewers.length === 0 ? (
          <TableEmpty colSpan={onRemove ? 5 : 4}>Nobody in this group.</TableEmpty>
        ) : (
          reviewers.map((reviewer) => (
            <TR key={reviewer.personId}>
              <TD>
                <ReviewerIdentity reviewer={reviewer} isYou={reviewer.personId === viewerId} />
              </TD>
              <TD>
                <DecisionBadge decision={reviewer.decision} />
              </TD>
              <TD>
                {reviewer.decidedAt ? (
                  formatDateTime(reviewer.decidedAt)
                ) : (
                  <span className="text-tertiary">—</span>
                )}
              </TD>
              <TD className="text-xs text-tertiary">{formatDateTime(reviewer.addedAt)}</TD>
              {onRemove ? (
                <TD>
                  {/* Offered for every row. The API refuses the owner and anyone
                      who has decided, with a sentence saying why — and that
                      refusal is a better teacher than a button that is quietly
                      absent. */}
                  <Button
                    size="sm"
                    variant="tertiary"
                    isPending={removingId === reviewer.personId}
                    onPress={() => onRemove(reviewer.personId)}
                  >
                    Remove
                  </Button>
                </TD>
              ) : null}
            </TR>
          ))
        )}
      </TBody>
    </Table>
  );
}

function Group({
  title,
  requirement,
  note,
  children,
}: {
  title: ReactNode;
  requirement: 'required' | 'advisory';
  note: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-secondary">
      <header className="flex flex-col gap-1 border-b border-secondary px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-primary">
          {title}
          <RequirementBadge requirement={requirement} />
        </h3>
        <p className="text-xs text-tertiary">{note}</p>
      </header>
      {children}
    </section>
  );
}

/**
 * Both groups, always both, even when one is empty.
 *
 * An advisory group that disappears when nobody is in it reads as a screen that
 * failed to load a section, and — worse — it hides the fact that nobody was
 * asked to weigh in. An empty required group is louder still: it is the vacuous
 * pass the gate refuses, and the reason it refuses shows up here as an empty
 * table under a heading that says publishing waits on it.
 */
export function ReviewerSets({ reviewers, viewerId, onRemove, removingId }: ReviewerTableProps) {
  const required = reviewers.filter((reviewer) => reviewer.requirement === 'required');
  const advisory = reviewers.filter((reviewer) => reviewer.requirement === 'advisory');

  return (
    <div className="flex flex-col gap-4">
      <Group
        title={`Publishing waits on ${required.length === 1 ? 'this reviewer' : `these ${required.length} reviewers`}`}
        requirement="required"
        note="Every one of them has to approve before this document can be published (§7.6). The owner is here by §7.3 whether or not anybody added them."
      >
        <ReviewerRows
          reviewers={required}
          viewerId={viewerId}
          {...(onRemove ? { onRemove } : {})}
          {...(removingId !== undefined ? { removingId } : {})}
        />
      </Group>

      <Group
        title={
          advisory.length === 0
            ? 'Nobody was asked to weigh in'
            : `${advisory.length} asked to weigh in`
        }
        requirement="advisory"
        note="Their opinion is wanted and their silence is not a block — publishing goes ahead with these outstanding. This is what §7.6 exists for: legal weighs in without becoming a gate."
      >
        <ReviewerRows
          reviewers={advisory}
          viewerId={viewerId}
          {...(onRemove ? { onRemove } : {})}
          {...(removingId !== undefined ? { removingId } : {})}
        />
      </Group>
    </div>
  );
}
